import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Lock, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useResource } from "@/hooks/use-resource";
import { AccessMatrix } from "@/components/settings/AccessMatrix";

const KIND = {
  lese: { label: "lese", cls: "bg-black/[0.05] text-foreground/70" },
  skrive: { label: "skrive", cls: "bg-[hsl(var(--brand-soft))] text-[hsl(208_90%_34%)]" },
  admin: { label: "admin-flagg", cls: "bg-[hsl(var(--navy)/0.1)] text-[hsl(var(--navy))]" },
  destruktiv: { label: "destruktiv", cls: "v-blocked" },
  ukjent: { label: "ikke modellert", cls: "v-assumed" },
};

function Roles({ roles, capabilities }) {
  const short = Object.fromEntries(capabilities.map((c) => [c.key, c.short]));
  return (
    <div className="flex flex-wrap gap-2" data-testid="access-roles">
      {roles.map((r) => (
        <div key={r.key} className={cn("rounded-[12px] border px-3 py-2 text-[12.5px]", r.known ? "border-black/[0.08] bg-white" : "border-dashed border-[hsl(var(--v-assumed)/0.6)] bg-[hsl(var(--v-assumed)/0.05)]")} data-testid={`access-role-${r.key}`} data-known={r.known ? "1" : "0"}>
          <div className="flex items-center gap-2 font-medium">{r.label}<span className="font-mono text-[11px] font-normal tabular-nums text-muted-foreground">{r.count} {r.count === 1 ? "person" : "personer"}</span></div>
          <div className="mt-0.5 text-[11.5px] text-muted-foreground">{r.known ? <>Gir i koden: {r.grants.map((g) => short[g] || g).join(", ")}</> : <span className="text-[hsl(var(--v-assumed))]">Ukjent for koden – gir ingen redigeringsrett</span>}</div>
        </div>
      ))}
    </div>
  );
}

function Rules({ capabilities }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-[16px] border border-black/[0.06] bg-white" data-testid="access-rules">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-[13.5px] font-medium hover:bg-black/[0.02]" data-testid="access-rules-toggle">
        <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-muted-foreground" /> Slik avgjøres det – tilgangskontroll som kode <span className="font-mono text-[11px] font-normal text-muted-foreground">{capabilities.length} regler</span></span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <ul className="divide-y divide-black/[0.05] border-t border-black/[0.06]" data-testid="access-rules-list">
          {capabilities.map((c) => (
            <li key={c.key} className="px-4 py-3 text-[13px]" data-testid={`access-cap-${c.key}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn("v-badge", KIND[c.kind]?.cls)}>{KIND[c.kind]?.label || c.kind}</span>
                <span className="font-medium">{c.label}</span>
              </div>
              <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{c.rule}</p>
              {c.source ? <div className="mt-1 font-mono text-[11px] text-muted-foreground/80">kilde: {c.source}</div> : null}
              {c.endpoints?.length ? <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 font-mono text-[10.5px] text-muted-foreground/70">{c.endpoints.map((e) => <span key={e}>{e}</span>)}</div> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Innstillinger → Tilgang: who may do what, read straight from the server's gate code. Nothing here changes anything. */
export function AccessOverview() {
  const mobile = !useMediaQuery("(min-width: 768px)");
  const { data, loading, error } = useResource("/team/access");
  if (error) return <p className="text-[13px] text-[hsl(var(--v-blocked))]" data-testid="access-error">{String(error)}</p>;
  if (loading && !data) return <div className="text-[13px] text-muted-foreground" data-testid="access-loading">Leser tilgangskoden…</div>;
  const people = data?.people || [];
  const caps = data?.capabilities || [];
  const unknownCount = people.reduce((n, p) => n + Object.values(p.access || {}).filter((v) => v.allowed === null).length, 0);
  return (
    <div className="space-y-3" data-testid="access-overview" data-readonly={data?.readonly ? "1" : "0"}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[14px] border border-black/[0.06] bg-[hsl(210_20%_98.5%)] px-4 py-2.5 text-[12.5px] text-foreground/80" data-testid="access-note">
        <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1">{data?.note}{data?.can_admin ? <> <Link to="/settings/brukere" className="font-medium text-foreground underline-offset-2 hover:underline" data-testid="access-users-link">Åpne Brukere →</Link></> : null}</span>
        <span className="inline-flex items-center gap-3 text-[11.5px] text-muted-foreground"><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[hsl(var(--v-ok))]" /> ja</span><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-black/[0.18]" /> nei</span><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full border border-dashed border-[hsl(var(--v-assumed))]" /> ukjent <span className="font-mono tabular-nums" data-testid="access-unknown-count">{unknownCount}</span></span></span>
      </div>
      <div className="rounded-[16px] border border-black/[0.06] bg-white">
        <AccessMatrix people={people} capabilities={caps} me={data?.me} mobile={mobile} />
      </div>
      <Roles roles={data?.roles || []} capabilities={caps} />
      <Rules capabilities={caps} />
      <ul className="space-y-1 text-[12px] leading-relaxed text-muted-foreground" data-testid="access-facts">
        {(data?.facts || []).map((f, i) => <li key={i} className="flex gap-2"><span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-black/30" />{f}</li>)}
      </ul>
    </div>
  );
}
